/**
 * McpTools — registers all Meshpit commands and state-query tools
 * as WebMCP tools on navigator.modelContext.
 *
 * Each toolbar action, code-insertion, viewer toggle, parameter action,
 * file action, and navigation command is exposed as a callable MCP tool.
 * Additionally, read/write tools are provided for the SCAD editor content
 * and the current parameter values.
 *
 * Usage: mount this component anywhere inside <WebMCPProvider> and inside
 * the providers that hold the state you want to expose (SceneSettingsContext,
 * BlobContext, etc.).
 */

import { useMcpTool } from 'webmcp-react';
import { z } from 'zod';
import type { Command } from '@/commands/types';
import type { Parameter } from '@shared/types';
import type { ViewerState } from '@/components/viewer/toolbar/ViewerToolbar';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert a command id like "insert.cube" → "insert_cube" for MCP tool names */
function toToolName(id: string): string {
  return id.replace(/[^a-zA-Z0-9_]/g, '_');
}

/** Build a text response for MCP */
function text(msg: string) {
  return { content: [{ type: 'text' as const, text: msg }] };
}

// ---------------------------------------------------------------------------
// McpCommandTool
// Single-command component — one useMcpTool call per Command.
// Rendered as a list by McpTools so that hooks rules are satisfied.
// ---------------------------------------------------------------------------

function McpCommandTool({ cmd }: { cmd: Command }) {
  const isToggle = cmd.getState !== undefined;

  useMcpTool({
    name: toToolName(cmd.id),
    description:
      cmd.keywords && cmd.keywords.length > 0
        ? `${cmd.label}. Category: ${cmd.category}. Keywords: ${cmd.keywords.join(', ')}.${isToggle ? ' This is a toggle — calling it flips the current state.' : ''}`
        : `${cmd.label}. Category: ${cmd.category}.${isToggle ? ' This is a toggle — calling it flips the current state.' : ''}`,
    input: z.object({}),
    annotations: {
      title: cmd.label,
      readOnlyHint: false,
      idempotentHint: isToggle,
    },
    handler: async () => {
      const stateBefore = isToggle ? cmd.getState!() : undefined;
      cmd.execute();
      const stateAfter = isToggle ? cmd.getState!() : undefined;

      if (isToggle) {
        return text(
          `${cmd.label}: ${stateAfter ? 'ON' : 'OFF'} (was ${stateBefore ? 'ON' : 'OFF'})`,
        );
      }
      return text(`Executed: ${cmd.label}`);
    },
  });

  return null;
}

// ---------------------------------------------------------------------------
// McpStateTool — get current SCAD code
// ---------------------------------------------------------------------------

function McpGetCodeTool({ scadCode }: { scadCode: string }) {
  useMcpTool({
    name: 'get_current_code',
    description: 'Returns the current OpenSCAD source code in the editor.',
    input: z.object({}),
    annotations: {
      title: 'Get Current Code',
      readOnlyHint: true,
      idempotentHint: true,
    },
    handler: async () => text(scadCode || '(empty)'),
  });
  return null;
}

// ---------------------------------------------------------------------------
// McpSetCodeTool — replace the editor content
// ---------------------------------------------------------------------------

function McpSetCodeTool({ onSetCode }: { onSetCode: (code: string) => void }) {
  useMcpTool({
    name: 'set_code',
    description:
      'Replaces the entire OpenSCAD source code in the editor with the provided code. The model will recompile automatically.',
    input: z.object({
      code: z
        .string()
        .describe('The complete OpenSCAD source code to load into the editor'),
    }),
    annotations: {
      title: 'Set Code',
      readOnlyHint: false,
      destructiveHint: true,
    },
    handler: async ({ code }) => {
      onSetCode(code);
      return text(
        `Code updated (${code.length} chars). The model will recompile automatically.`,
      );
    },
  });
  return null;
}

// ---------------------------------------------------------------------------
// McpGetParametersTool — list current parameter values
// ---------------------------------------------------------------------------

function McpGetParametersTool({ parameters }: { parameters: Parameter[] }) {
  useMcpTool({
    name: 'get_parameters',
    description:
      'Returns the current parameter definitions and their values. Each parameter has a name, type, current value, default value, and optional range constraints.',
    input: z.object({}),
    annotations: {
      title: 'Get Parameters',
      readOnlyHint: true,
      idempotentHint: true,
    },
    handler: async () => {
      if (parameters.length === 0) {
        return text(
          'No parameters defined. Add a /* [Parameters] */ block to your SCAD code to define them.',
        );
      }
      const lines = parameters.map((p) => {
        const parts = [
          `${p.name} (${p.type ?? 'unknown'}): ${JSON.stringify(p.value)}`,
        ];
        if (p.defaultValue !== undefined)
          parts.push(`default=${JSON.stringify(p.defaultValue)}`);
        if (p.range?.min !== undefined) parts.push(`min=${p.range.min}`);
        if (p.range?.max !== undefined) parts.push(`max=${p.range.max}`);
        if (p.range?.step !== undefined) parts.push(`step=${p.range.step}`);
        if (p.options && p.options.length > 0) {
          parts.push(`options=[${p.options.map((o) => o.value).join(', ')}]`);
        }
        if (p.group) parts.push(`group="${p.group}"`);
        return parts.join(' | ');
      });
      return text(lines.join('\n'));
    },
  });
  return null;
}

// ---------------------------------------------------------------------------
// McpSetParameterTool — change a single parameter value
// ---------------------------------------------------------------------------

function McpSetParameterTool({
  parameters,
  onParametersChange,
}: {
  parameters: Parameter[];
  onParametersChange: (params: Parameter[]) => void;
}) {
  useMcpTool({
    name: 'set_parameter',
    description:
      'Sets the value of a named parameter. The model recompiles automatically after the change.',
    input: z.object({
      name: z
        .string()
        .describe(
          'The parameter name (case-sensitive, as shown in get_parameters)',
        ),
      value: z
        .union([z.string(), z.number(), z.boolean()])
        .describe(
          'The new value. Use a number for numeric/slider parameters, boolean for checkboxes, string for dropdowns/colors/fonts.',
        ),
    }),
    annotations: { title: 'Set Parameter', readOnlyHint: false },
    handler: async ({ name, value }) => {
      const param = parameters.find((p) => p.name === name);
      if (!param) {
        const names = parameters.map((p) => p.name).join(', ');
        return {
          isError: true,
          content: [
            {
              type: 'text' as const,
              text: `Parameter "${name}" not found. Available: ${names || '(none)'}`,
            },
          ],
        };
      }
      const updated = parameters.map((p) =>
        p.name === name ? { ...p, value: value as Parameter['value'] } : p,
      );
      onParametersChange(updated);
      return text(`Parameter "${name}" set to ${JSON.stringify(value)}`);
    },
  });
  return null;
}

// ---------------------------------------------------------------------------
// McpGetViewerStateTool
// ---------------------------------------------------------------------------

function McpGetViewerStateTool({ viewerState }: { viewerState: ViewerState }) {
  useMcpTool({
    name: 'get_viewer_state',
    description:
      'Returns the current state of all 3D viewer toggles (grid, axis, wireframe, SSAO, bloom, etc.).',
    input: z.object({}),
    annotations: {
      title: 'Get Viewer State',
      readOnlyHint: true,
      idempotentHint: true,
    },
    handler: async () => {
      const lines = [
        `grid: ${viewerState.showGrid}`,
        `axis: ${viewerState.showAxis}`,
        `orthographic: ${viewerState.isOrthographic}`,
        `wireframe_opacity: ${viewerState.wireframeOpacity}`,
        `moving_light: ${viewerState.showMovingLight}`,
        `presentation_mode: ${viewerState.presentationMode}`,
        `measurement: ${viewerState.measurementEnabled}`,
        `show_excluded: ${viewerState.showExcluded}`,
        `multi_view: ${viewerState.multiView}`,
        `pip: ${viewerState.showPiP}`,
        `background_color: ${viewerState.backgroundColor}`,
      ];
      return text(lines.join('\n'));
    },
  });
  return null;
}

// ---------------------------------------------------------------------------
// McpGetModelInfoTool
// ---------------------------------------------------------------------------

function McpGetModelInfoTool({
  title,
  isPublic,
  isDirty,
  lastSavedAt,
  hasModel,
}: {
  title: string;
  isPublic: boolean;
  isDirty: boolean;
  lastSavedAt: string | null;
  hasModel: boolean;
}) {
  useMcpTool({
    name: 'get_model_info',
    description:
      'Returns metadata about the currently open model: title, visibility, save status, and whether it has been saved to the server.',
    input: z.object({}),
    annotations: {
      title: 'Get Model Info',
      readOnlyHint: true,
      idempotentHint: true,
    },
    handler: async () => {
      const lines = [
        `title: ${title}`,
        `saved_to_server: ${hasModel}`,
        `visibility: ${isPublic ? 'public' : 'private'}`,
        `unsaved_changes: ${isDirty}`,
        `last_saved: ${lastSavedAt ?? 'never'}`,
      ];
      return text(lines.join('\n'));
    },
  });
  return null;
}

// ---------------------------------------------------------------------------
// McpTools — main export
// ---------------------------------------------------------------------------

export interface McpToolsProps {
  /** All available commands from useCommandRegistry */
  commands: Command[];
  /** Current OpenSCAD source */
  scadCode: string;
  /** Replace editor content */
  onSetCode: (code: string) => void;
  /** Current parameter values */
  parameters: Parameter[];
  /** Update parameters */
  onParametersChange: (params: Parameter[]) => void;
  /** Current viewer toggle state */
  viewerState: ViewerState;
  /** Model metadata */
  modelInfo: {
    title: string;
    isPublic: boolean;
    isDirty: boolean;
    lastSavedAt: string | null;
    hasModel: boolean;
  };
}

export function McpTools({
  commands,
  scadCode,
  onSetCode,
  parameters,
  onParametersChange,
  viewerState,
  modelInfo,
}: McpToolsProps) {
  return (
    <>
      {/* One MCP tool per command */}
      {commands.map((cmd) => (
        <McpCommandTool key={cmd.id} cmd={cmd} />
      ))}

      {/* State query tools */}
      <McpGetCodeTool scadCode={scadCode} />
      <McpSetCodeTool onSetCode={onSetCode} />
      <McpGetParametersTool parameters={parameters} />
      <McpSetParameterTool
        parameters={parameters}
        onParametersChange={onParametersChange}
      />
      <McpGetViewerStateTool viewerState={viewerState} />
      <McpGetModelInfoTool {...modelInfo} />
    </>
  );
}
